<template>
    <LoadingModal
        v-if="loading"
        @close="$emit('abort')"
    />
    <SetNotificationEnablementModal
        v-else-if="configuration"
        open
        :type="type"
        :initial-value="Boolean(configuration.initEnabled)"
        :submitting="submitting"
        @close="$emit('abort')"
        @submit="onSubmit"
    />
</template>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { getSnackbarActions } from '@evidentid/dashboard-commons/modules/snackbar';
    import {
        NotificationConfiguration,
        ReminderlessNotificationType,
    } from '@evidentid/tprm-portal-lib/models/notification-configuration';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import LoadingModal from '@/components/loading-modal/LoadingModal.vue';

    import SetNotificationEnablementModal from '../components/SetNotificationEnablementModal.vue';
    import { useNotificationsConfigurationStore } from '../store';

    export default defineComponent({
        name: 'SetNotificationEnablementProcedure',
        components: {
            LoadingModal,
            SetNotificationEnablementModal,
        },
        props: {
            // ProcedureTarget passes `:uuid` to every procedure it renders. Nothing here
            // reads it, but both roots below are conditional, so before the configuration
            // loads this renders a comment node -- an undeclared `uuid` would then have
            // nowhere to land and Vue warns about extraneous non-prop attributes.
            uuid: {
                type: String,
                default: '',
            },
            rpName: {
                type: String,
                required: true,
            },
            type: {
                type: String as () => ReminderlessNotificationType,
                required: true,
            },
        },
        emits: [ 'abort', 'finish' ],
        computed: {
            notificationsStore() {
                return useNotificationsConfigurationStore();
            },
            configuration(): NotificationConfiguration | null {
                return this.notificationsStore.emailTemplates?.[this.rpName]?.[this.type] || null;
            },
            loadStatus(): OperationStatus {
                return this.notificationsStore.loadEmailTemplatesStatus?.[this.rpName]
                    || OperationStatus.uninitialized;
            },
            updateStatus(): OperationStatus {
                return this.notificationsStore.updateEmailTemplates?.[this.rpName]?.[this.type]
                    || OperationStatus.uninitialized;
            },
            loading(): boolean {
                return [ OperationStatus.uninitialized, OperationStatus.loading ].includes(this.loadStatus);
            },
            submitting(): boolean {
                return this.updateStatus === OperationStatus.loading;
            },
            // A relying party configured before this notification type existed has no stored
            // template, and there is nothing to enable until one is created.
            unavailable(): boolean {
                return this.loadStatus === OperationStatus.error
                    || (this.loadStatus === OperationStatus.success && !this.configuration);
            },
        },
        watch: {
            unavailable: {
                immediate: true,
                handler(unavailable: boolean): void {
                    if (unavailable) {
                        this.$emit('abort');
                    }
                },
            },
        },
        mounted(): void {
            // The settings page loads these on mount, so only fetch when this modal is opened
            // without them; refetching unconditionally would flash the loading modal.
            if ([ OperationStatus.uninitialized, OperationStatus.error ].includes(this.loadStatus)) {
                this.notificationsStore.loadEmailTemplates({ rpName: this.rpName });
            }
        },
        methods: {
            async onSubmit(initEnabled: boolean): Promise<void> {
                await this.notificationsStore.updateNotificationEnablement({
                    rpName: this.rpName,
                    type: this.type,
                    initEnabled,
                });

                if (this.updateStatus === OperationStatus.success) {
                    await Promise.all([
                        this.notificationsStore.loadEmailTemplates({ rpName: this.rpName }),
                        getSnackbarActions().displaySnackbar({
                            message: 'Updated notifications successfully.',
                            success: true,
                        }),
                    ]);
                    this.$emit('finish');
                }
            },
        },
    });
</script>
